import { Link } from '@components/atoms/Link/Link';
import { ShowAllTitle } from '@components/atoms/ShowAllTitle/ShowAllTitle';
import { Carousel } from '@components/molecules/Carousel/Carousel';
import { useScreenPosition } from '@components/molecules/GrowingElement/useScreenPosition';
import type { PageContentElementTvmshorts as PageContentElementTvmshortsType } from '@src/lib/graphql/generated';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

type Short = NonNullable<PageContentElementTvmshortsType['shorts']>[number];

/** Where the tile grows on hover, so it never expands past the carousel's clipped edges */
const screenPositionStyle = {
  left: 'group-hover:ml-0 group-hover:-mr-8',
  center: 'group-hover:-mx-4',
  right: 'group-hover:mr-0 group-hover:-ml-8',
};

export default function PageContentElementTvmshorts(element: PageContentElementTvmshortsType) {
  if (!element?.shorts?.length) return null;

  return <ShortsLane title={element.title} titleLink={element.title_link} shorts={element.shorts} />;
}

/**
 * `PageBody` invokes page elements as plain functions rather than as JSX, so their hooks would land
 * in its own hook list — the lane keeps its state in a real component instead.
 */
function ShortsLane({ title, titleLink, shorts }: { title?: string; titleLink?: string; shorts: Short[] }) {
  const [lastScroll, setLastScroll] = useState(0);

  return (
    <div className="relative pl-4 sm:pl-8 lg:pl-16">
      <ShowAllTitle title={title} link={titleLink} />
      <Carousel classProps={{ root: 'z-10' }} onScrollComplete={() => setLastScroll((prev) => prev + 1)}>
        {shorts.map((short) => (
          <ShortTeaser key={short.id} short={short} lastScroll={lastScroll} />
        ))}
      </Carousel>
    </div>
  );
}

function ShortTeaser({ short, lastScroll }: { short: Short; lastScroll: number }) {
  const { domElement, screenPosition } = useScreenPosition(lastScroll);

  return (
    <Link
      href={`/shorts/${short.slug || short.id}`}
      className={twMerge(
        'group relative z-10 mb-8 block aspect-short w-min min-w-40 shrink-0 pb-11 hover:z-40 md:mb-16',
      )}
    >
      <div
        ref={domElement}
        className={twMerge(
          'absolute inset-0 aspect-short transition-all duration-150 group-hover:-m-4 group-hover:-mt-8',
          screenPositionStyle[screenPosition],
        )}
      >
        {/* Shorts carry no separate thumbnail — fairu renders a still of the video whenever the
            image loader asks for a sized variant, so the video url doubles as the poster source */}
        <Image
          src={short.video?.url}
          alt={`Thumbnail von ${short.title}`}
          copyright=""
          fill
          sizes="192px"
          classProps={{ root: 'rounded-lg' }}
        />
        <div className="hyphenate line-clamp-3 w-full px-2 pt-2 font-herokid font-medium text-gray-900 text-sm group-hover:text-foreground md:pt-5 md:text-base dark:text-foreground">
          {short.title}
        </div>
      </div>
    </Link>
  );
}
